import { startCase } from "lodash";
import { useMemo } from "react";

import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { getReadableDateTimeString } from "@covertree/utils";
import { Resident } from "@graphql/api";

import PropertiesGrid from "@covertree/ui/content/PropertiesGrid";
import TabAccordion from "@components/TabAccordion";

interface Props {
  isLoading?: boolean;
  resident?: Resident;
}

function parseAdditionalData(additionalData: string) {
  try {
    const parsedAdditionalData: unknown = JSON.parse(additionalData);

    if (
      !parsedAdditionalData ||
      typeof parsedAdditionalData !== "object" ||
      Array.isArray(parsedAdditionalData)
    ) {
      return [];
    }

    return Object.entries(parsedAdditionalData).map(([key, value]) => ({
      label: `${key}:`,
      value: String(value),
    }));
  } catch {
    return [];
  }
}

export function PhysicalMailNotificationEventsTabContent({
  isLoading,
  resident,
}: Props) {
  const residentEvents = resident?.physicalMailNotificationEvents;
  const physicalMailNotificationEvents = useMemo(
    () =>
      (residentEvents ?? []).map((event) => ({
        ...event,
        additionalData: parseAdditionalData(event.additionalData),
      })),
    [residentEvents]
  );

  if (!residentEvents && isLoading) {
    return <Skeleton width="100%" height={76} variant="rectangular" />;
  }

  if (!residentEvents?.length) {
    return (
      <Typography variant="subtitle1">
        Resident has no physical mail notification history.
      </Typography>
    );
  }

  return (
    <Stack direction="column" spacing={2}>
      {physicalMailNotificationEvents.map((event) => (
        <TabAccordion
          key={event.id}
          id={event.id}
          title={`${startCase(event.eventType)} - ${getReadableDateTimeString(event.eventDate)}`}>
          <Stack display="flex" width="100%" spacing={2}>
            <PropertiesGrid properties={event.additionalData} />
          </Stack>
        </TabAccordion>
      ))}
    </Stack>
  );
}
